import type { HttpClient } from "@dl/web-core-js/api";
import type { Period } from "./reporting";

/**
 * Customer-service data: per-product request counts grouped by motive, the
 * parts consumed by service work, and the raw request lines. All read-only.
 */

export interface ServiceMotiveDTO {
  id: number;
  no?: string | null;
  description?: string | null;
}

export interface ProductMotiveServiceRequestDTO {
  product_sku?: string | null;
  motive?: ServiceMotiveDTO | null;
  request_quantity?: number;
  part_quantity?: number;
}

export interface ProductServiceDTO {
  product_sku?: string | null;
  period_from?: string | null;
  period_to?: string | null;
  motive_service_requests?: ProductMotiveServiceRequestDTO[];
}

export interface ServicePartDTO {
  part?: { id?: number; no?: string | null; description1?: string | null } | null;
  quantity?: number;
}

export interface ServiceRequestLineDTO {
  id: number;
  order_no?: number;
  order_date?: string | null;
  order_status?: string | null;
  part?: { id?: number; no?: string | null; description1?: string | null } | null;
  motive?: ServiceMotiveDTO | null;
  quantity?: number;
  invoice_name?: string | null;
  client_order_no?: string | null;
}

/** The motives the main grid columns show, in Vaadin's allow-list order. */
export const SERVICE_MOTIVE_COLUMNS = ["BQ", "BT", "DP", "MPQ", "MSQ", "L", "F"];

/** Sum of request quantities across every motive of one product. */
export function requestTotal(product: ProductServiceDTO, motiveId?: number): number {
  const requests = product.motive_service_requests ?? [];
  if (motiveId === undefined) {
    return requests.reduce((sum, entry) => sum + (entry.request_quantity ?? 0), 0);
  }
  return requests.find((entry) => entry.motive?.id === motiveId)?.request_quantity ?? 0;
}

/** Sum of part quantities across every motive of one product. */
export function partTotal(product: ProductServiceDTO, motiveId?: number): number {
  const requests = product.motive_service_requests ?? [];
  if (motiveId === undefined) {
    return requests.reduce((sum, entry) => sum + (entry.part_quantity ?? 0), 0);
  }
  return requests.find((entry) => entry.motive?.id === motiveId)?.part_quantity ?? 0;
}

export class ServiceClient {
  constructor(private readonly http: HttpClient) {}

  motives(): Promise<ServiceMotiveDTO[]> {
    return this.http.get<ServiceMotiveDTO[]>("/service/motive");
  }

  /** Per-product motive/request rollup for a period — the main grid rows. */
  requestsByPeriod(period: Period): Promise<ProductServiceDTO[]> {
    return this.http.get<ProductServiceDTO[]>("/service/product/motive/request/period", {
      query: { date_from: period.from, date_to: period.to },
    });
  }

  /** One product's rollup for a period — the per-year chart series. */
  requestsForProduct(sku: string, period: Period): Promise<ProductServiceDTO> {
    return this.http.get<ProductServiceDTO>(
      `/service/product/motive/request/product/${encodeURIComponent(sku)}`,
      { query: { date_from: period.from, date_to: period.to } },
    );
  }

  /** Parts consumed servicing one product over a period. */
  partsForProduct(sku: string, period: Period): Promise<ServicePartDTO[]> {
    return this.http.get<ServicePartDTO[]>(
      `/service/part/period/product/${encodeURIComponent(sku)}`,
      { query: { date_from: period.from, date_to: period.to } },
    );
  }

  /** Raw request lines for one product over a period. */
  requestLinesForProduct(sku: string, period: Period): Promise<ServiceRequestLineDTO[]> {
    return this.http.get<ServiceRequestLineDTO[]>(
      `/service/request/line/product/${encodeURIComponent(sku)}`,
      { query: { date_from: period.from, date_to: period.to } },
    );
  }

  /** Total request-line count over a period — the KPI numerator. */
  requestLineCount(period: Period): Promise<number> {
    return this.http.get<number>("/service/request/line/period/count", {
      query: { date_from: period.from, date_to: period.to },
    });
  }
}
